import { toast } from '@/components/ui/toast';

/*
 * App toast ergonomics (spec §7.2 — mutations confirm via toast).
 *
 * The Base UI toast primitive (components/ui/toast) exposes an imperative
 * manager with a single `add({ title, type })`. This composed layer adds the
 * app opinion: a `notify` helper with success/error/info/warning shorthands and
 * our default copy conventions. Keeping this here (not in ui/) leaves the
 * primitive pristine and puts ergonomics where app opinions belong.
 *
 * The `Toaster` host component lives in `Toaster.tsx`; this non-component module
 * holds `notify` so each file exports a single kind (react-refresh).
 */
export interface NotifyOptions {
  description?: string;
  /** ms before auto-dismiss; 0 keeps it until closed. */
  timeout?: number;
}

function emit(
  type: 'success' | 'error' | 'info' | 'warning',
  title: string,
  options?: NotifyOptions,
): string {
  return toast.add({
    title,
    type,
    ...(options?.description ? { description: options.description } : {}),
    ...(options?.timeout !== undefined ? { timeout: options.timeout } : {}),
  });
}

export const notify = {
  success: (title: string, options?: NotifyOptions) =>
    emit('success', title, options),
  error: (title: string, options?: NotifyOptions) =>
    emit('error', title, options),
  info: (title: string, options?: NotifyOptions) =>
    emit('info', title, options),
  warning: (title: string, options?: NotifyOptions) =>
    emit('warning', title, options),
  dismiss: (id?: string) => toast.close(id),
};
